// src/pages/ProjectsPage.js

import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ThemeContext } from '../context/ThemeContext';
import SectionTitle from '../components/common/SectionTitle';
import Button from '../components/common/Button';
import debounce from 'lodash/debounce';
import { projectService } from '../api';
import { FaRegStar, FaStar } from 'react-icons/fa';
import { toast } from 'react-toastify';

// Styled components
const ProjectsSection = styled.section`
  padding: 5rem 2rem;
  min-height: 80vh;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FilterContainer = styled(motion.div)`
  background: ${props => props.theme.card.background};
  border-radius: 8px;
  box-shadow: ${props => props.theme.card.shadow};
  padding: 1.5rem;
  margin-bottom: 3rem;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FilterGroup = styled.div``;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.text};
  font-weight: 500;
`;

const FilterSelect = styled.select`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid ${props => props.theme.text}30;
  border-radius: 4px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid ${props => props.theme.text}30;
  border-radius: 4px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const FilterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  
  @media (max-width: 768px) {
    justify-content: stretch;
    
    button {
      flex: 1;
    }
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
`;

const ProjectCard = styled(motion.div)`
  background: ${props => props.theme.card.background};
  border-radius: 8px;
  box-shadow: ${props => props.theme.card.shadow};
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    box-shadow: ${props => props.theme.card.hover};
  }
`;

const ProjectImage = styled.div`
  width: 100%;
  height: 200px;
  background-image: url(${props => props.src || '/assets/images/placeholder.jpg'});
  background-size: cover;
  background-position: center;
  position: relative;
`;

const ProjectStatus = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  color: white;
  background-color: ${props => {
    switch (props.status) {
      case 'completed':
        return props.theme.success;
      case 'in_progress':
        return props.theme.primary;
      case 'planning':
        return props.theme.accent;
      case 'on_hold':
        return '#f59e0b';
      default:
        return props.theme.text;
    }
  }};
`;

const OpenForApplications = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  background-color: ${props => props.theme.success};
  color: white;
`;

const ProjectContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ProjectTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 0.8rem;
  color: ${props => props.theme.text};
  
  a {
    color: inherit;
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
      color: ${props => props.theme.primary};
    }
  }
`;

const ProjectDescription = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.text};
  opacity: 0.8;
  margin-bottom: 1.5rem;
  flex: 1;
`;

const ProjectMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  border-top: 1px solid ${props => props.theme.text}10;
  padding-top: 1rem;
`;

const ProjectClient = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.text};
  opacity: 0.7;
`;

const TechTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 1rem 0;
`;

const TechTag = styled.span`
  display: inline-block;
  padding: 0.2rem 0.5rem;
  background: ${props => props.theme.primary}20;
  color: ${props => props.theme.primary};
  border-radius: 4px;
  font-size: 0.8rem;
`;

const ProjectFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
`;

const ProjectDate = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.text};
  opacity: 0.7;
`;

const NoResults = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${props => props.theme.text};
  opacity: 0.7;
  font-size: 1.2rem;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 3rem;
  gap: 0.5rem;
`;

const PaginationButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  border: 1px solid ${props => props.active ? props.theme.primary : props.theme.text + '30'};
  background: ${props => props.active ? props.theme.primary : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.text};
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => props.active ? props.theme.primary : props.theme.backgroundSecondary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RatingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const StarRating = styled.div`
  display: flex;
  align-items: center;
  gap: 0.2rem;
  cursor: pointer;
`;

const StarIcon = styled.div`
  color: ${props => props.filled ? '#FFD700' : '#ccc'};
  transition: transform 0.2s ease;
  
  &:hover {
    transform: scale(1.2);
  }
`;

const RatingCount = styled.span`
  font-size: 0.8rem;
  color: ${props => props.theme.text};
  opacity: 0.8;
  margin-left: 0.5rem;
`;

const ParticipationMethods = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const ParticipationBadge = styled.span`
  font-size: 0.75rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  background-color: ${props => props.theme.backgroundSecondary};
  color: ${props => props.theme.text};
`;

// Animations variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const ProjectsPage = () => {
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [technologies, setTechnologies] = useState([]);
  const itemsPerPage = 6;
  
  // Filters state
  const [filters, setFilters] = useState({
    status: '',
    technology: '',
    isOpenForApplications: '',
    sortBy: 'newest'
  });
  
  // Use InView hook for animations
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((term) => {
      applyFilters(term, filters);
    }, 500),
    [filters]
  );
  
  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };
  
  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);
    applyFilters(searchTerm, newFilters);
  };
  
  // Rate a project
  const handleRateProject = async (projectId, rating) => {
    try {
      await projectService.rateProject(projectId, { rating });
      // Update projects list with new rating
      const updatedProjects = projects.map(project => {
        if (project.id === projectId) {
          // Calculate new average rating
          const totalRatings = project.ratings_count || 0;
          const currentRatingSum = (project.average_rating || 0) * totalRatings;
          const newTotalRatings = totalRatings + 1;
          const newAverageRating = (currentRatingSum + rating) / newTotalRatings;
          
          return {
            ...project,
            average_rating: newAverageRating,
            ratings_count: newTotalRatings,
            user_rating: rating
          };
        }
        return project;
      });
      
      setProjects(updatedProjects);
      setFilteredProjects(applyFiltersToProjects(searchTerm, filters, updatedProjects));
      toast.success(t('ratingSubmitted'));
    } catch (error) {
      console.error("Error rating project:", error);
      toast.error(t('errorRatingProject'));
    }
  };
  
  // Reset filters
  const resetFilters = () => {
    setFilters({
      status: '',
      technology: '',
      isOpenForApplications: '',
      sortBy: 'newest'
    });
    setSearchTerm('');
    applyFilters('', {
      status: '',
      technology: '',
      isOpenForApplications: '',
      sortBy: 'newest'
    });
  };
  
  // Apply filters and search to projects
  const applyFiltersToProjects = (search, currentFilters, projectsList) => {
    let result = [...projectsList];
    
    // Apply search
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(project => 
        (project.title && project.title.toLowerCase().includes(searchLower)) ||
        (project.description && project.description.toLowerCase().includes(searchLower)) ||
        (project.client && project.client.toLowerCase().includes(searchLower)) ||
        (project.technologies && project.technologies.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply status filter
    if (currentFilters.status) {
      result = result.filter(project => project.status === currentFilters.status);
    }
    
    // Apply technology filter
    if (currentFilters.technology) {
      result = result.filter(project => 
        project.technologies && project.technologies.toLowerCase().includes(currentFilters.technology.toLowerCase())
      );
    }
    
    // Apply open for applications filter
    if (currentFilters.isOpenForApplications) {
      const isOpen = currentFilters.isOpenForApplications === 'true';
      result = result.filter(project => project.is_open_for_applications === isOpen);
    }
    
    // Apply sorting
    switch (currentFilters.sortBy) {
      case 'newest':
        result.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
        break;
      case 'oldest':
        result.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
        break;
      case 'alphabetical':
        result.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'rating':
        result.sort((a, b) => (b.average_rating || 0) - (a.average_rating || 0));
        break;
      default:
        break;
    }
    
    return result;
  };
  
  const applyFilters = (search, currentFilters) => {
    const result = applyFiltersToProjects(search, currentFilters, projects);
    setFilteredProjects(result);
    setTotalPages(Math.ceil(result.length / itemsPerPage));
    setCurrentPage(1); // Reset to first page when filters change
  };
  
  // Handle pagination
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Get current projects
  const getCurrentProjects = () => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return filteredProjects.slice(indexOfFirstItem, indexOfLastItem);
  };
  
  // Extract unique technologies from projects
  const extractTechnologies = (projectsList) => {
    const techSet = new Set();
    
    projectsList.forEach(project => {
      if (project.technologies) {
        const techs = project.technologies.split(',').map(tech => tech.trim());
        techs.forEach(tech => {
          if (tech) techSet.add(tech);
        });
      }
    });
    
    return Array.from(techSet).sort();
  };
  
  // Load projects data
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        
        const response = await projectService.getAll();
        const projectsData = response.data || [];
        
        setProjects(projectsData);
        setFilteredProjects(projectsData);
        setTotalPages(Math.ceil(projectsData.length / itemsPerPage));
        
        // Extract technologies for filter
        const techsList = extractTechnologies(projectsData);
        setTechnologies(techsList);
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching projects:", error);
        setError(t('errorLoadingProjects'));
        setLoading(false);
      }
    };
    
    fetchProjects();
  }, [t, itemsPerPage]);
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return t('ongoing');
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(navigator.language, options);
  };
  
  // Translate project status
  const getStatusLabel = (status) => {
    switch (status) {
      case 'planning':
        return t('planning');
      case 'in_progress':
        return t('inProgress');
      case 'completed':
        return t('completed');
      case 'on_hold':
        return t('onHold');
      default:
        return status;
    }
  };
  
  // Render star rating component
  const renderStarRating = (project) => {
    const rating = project.average_rating || 0;
    const count = project.ratings_count || 0;
    
    return (
      <RatingContainer>
        <StarRating>
          {[1, 2, 3, 4, 5].map((star) => (
            <StarIcon 
              key={star} 
              filled={star <= Math.round(rating)}
              onClick={() => handleRateProject(project.id, star)}
            >
              {star <= Math.round(rating) ? <FaStar /> : <FaRegStar />}
            </StarIcon>
          ))}
        </StarRating>
        <RatingCount theme={theme}>({count})</RatingCount>
      </RatingContainer>
    );
  };
  
  return (
    <ProjectsSection>
      <Container>
        <SectionTitle
          title={t('ourProjects')}
          subtitle={t('ourProjectsSubtitle')}
        />
        
        <FilterContainer 
          theme={theme}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <FilterGrid>
            <FilterGroup>
              <FilterLabel htmlFor="search" theme={theme}>{t('searchProjects')}</FilterLabel>
              <SearchInput
                type="text"
                id="search"
                placeholder={t('searchPlaceholder')}
                value={searchTerm}
                onChange={handleSearchChange}
                theme={theme}
              />
            </FilterGroup>
            
            <FilterGroup>
              <FilterLabel htmlFor="status" theme={theme}>{t('status')}</FilterLabel>
              <FilterSelect
                id="status"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                theme={theme}
              >
                <option value="">{t('allStatuses')}</option>
                <option value="planning">{t('planning')}</option>
                <option value="in_progress">{t('inProgress')}</option>
                <option value="completed">{t('completed')}</option>
                <option value="on_hold">{t('onHold')}</option>
              </FilterSelect>
            </FilterGroup>
            
            <FilterGroup>
              <FilterLabel htmlFor="technology" theme={theme}>{t('technology')}</FilterLabel>
              <FilterSelect
                id="technology"
                name="technology"
                value={filters.technology}
                onChange={handleFilterChange}
                theme={theme}
              >
                <option value="">{t('allTechnologies')}</option>
                {technologies.map((tech, index) => (
                  <option key={index} value={tech}>{tech}</option>
                ))}
              </FilterSelect>
            </FilterGroup>
            
            <FilterGroup>
              <FilterLabel htmlFor="isOpenForApplications" theme={theme}>{t('applications')}</FilterLabel>
              <FilterSelect
                id="isOpenForApplications"
                name="isOpenForApplications"
                value={filters.isOpenForApplications}
                onChange={handleFilterChange}
                theme={theme}
              >
                <option value="">{t('allProjects')}</option>
                <option value="true">{t('openForApplications')}</option>
                <option value="false">{t('closedForApplications')}</option>
              </FilterSelect>
            </FilterGroup>
            
            <FilterGroup>
              <FilterLabel htmlFor="sortBy" theme={theme}>{t('sortBy')}</FilterLabel>
              <FilterSelect
                id="sortBy"
                name="sortBy"
                value={filters.sortBy}
                onChange={handleFilterChange}
                theme={theme}
              >
                <option value="newest">{t('newest')}</option>
                <option value="oldest">{t('oldest')}</option>
                <option value="alphabetical">{t('alphabetical')}</option>
                <option value="rating">{t('highestRated')}</option>
              </FilterSelect>
            </FilterGroup>
          </FilterGrid>
          
          <FilterActions>
            <Button
              variant="outline"
              onClick={resetFilters}
            >
              <i className="fas fa-undo"></i> {t('resetFilters')}
            </Button>
          </FilterActions>
        </FilterContainer>
        
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '5rem 0' }}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{ textAlign: 'center' }}
            >
              <svg width="50" height="50" viewBox="0 0 50 50">
                <circle 
                  cx="25" 
                  cy="25" 
                  r="20" 
                  fill="none" 
                  stroke={theme.primary} 
                  strokeWidth="3" 
                  strokeLinecap="round"
                >
                  <animateTransform 
                    attributeName="transform" 
                    type="rotate" 
                    from="0 25 25" 
                    to="360 25 25" 
                    dur="1.5s" 
                    repeatCount="indefinite"
                  />
                  <animate 
                    attributeName="stroke-dasharray" 
                    from="1, 150" 
                    to="90, 150" 
                    dur="1.5s" 
                    repeatCount="indefinite"
                  />
                  <animate 
                    attributeName="stroke-dashoffset" 
                    from="0" 
                    to="-35" 
                    dur="1.5s" 
                    repeatCount="indefinite"
                  />
                </circle>
              </svg>
              <p style={{ marginTop: '1rem', color: theme.text }}>{t('loadingProjects')}</p>
            </motion.div>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '3rem 0' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div style={{ fontSize: '4rem', color: theme.error, marginBottom: '1rem' }}>
                <i className="fas fa-exclamation-circle"></i>
              </div>
              <h2 style={{ fontSize: '1.8rem', color: theme.text, marginBottom: '1.5rem' }}>{error}</h2>
              <Button variant="primary" onClick={() => window.location.reload()}>
                <i className="fas fa-redo"></i> {t('tryAgain')}
              </Button>
            </motion.div>
          </div>
        ) : filteredProjects.length === 0 ? (
          <NoResults theme={theme}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>
                <i className="fas fa-search"></i>
              </div>
              <h3>{t('noProjectsFound')}</h3>
              <p>{t('tryDifferentFilters')}</p>
            </motion.div>
          </NoResults>
        ) : (
          <>
            <motion.div
              ref={ref}
              variants={staggerContainer}
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
            >
              <ProjectsGrid>
                <AnimatePresence>
                  {getCurrentProjects().map((project, index) => (
                    <motion.div
                      key={project.id}
                      variants={fadeInUp}
                      transition={{ duration: 0.5, delay: index * 0.05 }}
                      layout
                    >
                      <ProjectCard theme={theme}>
                        <ProjectImage src={project.image}>
                          <ProjectStatus theme={theme} status={project.status}>
                            {getStatusLabel(project.status)}
                          </ProjectStatus>
                          {project.is_open_for_applications && (
                            <OpenForApplications theme={theme}>
                              {t('openForApplications')}
                            </OpenForApplications>
                          )}
                        </ProjectImage>
                        
                        <ProjectContent>
                          <ProjectTitle theme={theme}>
                            <Link to={`/projects/${project.slug}`}>{project.title}</Link>
                          </ProjectTitle>
                          
                          {renderStarRating(project)}
                          
                          <ProjectDescription theme={theme}>
                            {project.short_description || project.description}
                          </ProjectDescription>
                          
                          {project.technologies && (
                            <TechTagsContainer>
                              {project.technologies.split(',').map((tech, i) => (
                                <TechTag key={i} theme={theme}>{tech.trim()}</TechTag>
                              ))}
                            </TechTagsContainer>
                          )}
                          
                          <ParticipationMethods>
                            {project.can_participate && (
                              <ParticipationBadge theme={theme}>
                                {t('participationAllowed')}
                              </ParticipationBadge>
                            )}
                            {project.can_mentor && (
                              <ParticipationBadge theme={theme}>
                                {t('mentoringNeeded')}
                              </ParticipationBadge>
                            )}
                            {project.can_fund && (
                              <ParticipationBadge theme={theme}>
                                {t('fundingOpen')}
                              </ParticipationBadge>
                            )}
                          </ParticipationMethods>
                          
                          <ProjectMeta theme={theme}>
                            {project.client && (
                              <ProjectClient theme={theme}>
                                <i className="fas fa-building" style={{ marginRight: '5px' }}></i>
                                {project.client}
                              </ProjectClient>
                            )}
                            <ProjectDate theme={theme}>
                              <i className="fas fa-calendar-alt" style={{ marginRight: '5px' }}></i>
                              {formatDate(project.start_date)}
                            </ProjectDate>
                          </ProjectMeta>
                          
                          <ProjectFooter>
                            <Button
                              variant="outline"
                              size="small"
                              to={`/projects/${project.id}`}
                            >
                              {t('viewDetails')}
                            </Button>
                            
                            {project.is_open_for_applications && (
                              <Button
                                variant="primary"
                                size="small"
                                to={`/projects/${project.id}/apply`}
                              >
                                {t('apply')}
                              </Button>
                            )}
                          </ProjectFooter>
                        </ProjectContent>
                      </ProjectCard>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </ProjectsGrid>
            </motion.div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <PaginationContainer>
                <PaginationButton
                  theme={theme}
                  onClick={() => paginate(1)}
                  disabled={currentPage === 1}
                >
                  <i className="fas fa-angle-double-left"></i>
                </PaginationButton>
                
                <PaginationButton
                  theme={theme}
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="fas fa-angle-left"></i>
                </PaginationButton>
                
                {Array.from({ length: totalPages }).map((_, index) => {
                  // Afficher les 2 pages avant et après la page actuelle, ainsi que la première et la dernière
                  if (
                    index === 0 ||
                    index === totalPages - 1 ||
                    (index >= currentPage - 2 && index <= currentPage + 1)
                  ) {
                    return (
                      <PaginationButton
                        key={index}
                        theme={theme}
                        active={currentPage === index + 1}
                        onClick={() => paginate(index + 1)}
                      >
                        {index + 1}
                      </PaginationButton>
                    );
                  } else if (
                    index === currentPage - 3 ||
                    index === currentPage + 2
                  ) {
                    return <span key={index} style={{ alignSelf: 'center' }}>...</span>;
                  }
                  return null;
                })}
              <PaginationButton
                  theme={theme}
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <i className="fas fa-angle-right"></i>
                </PaginationButton>
                
                <PaginationButton
                  theme={theme}
                  onClick={() => paginate(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  <i className="fas fa-angle-double-right"></i>
                </PaginationButton>
              </PaginationContainer>
            )}
          </>
        )}
      </Container>
    </ProjectsSection>
  );
};

export default ProjectsPage;